import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Trophy, X, Check } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook to fetch whether current user has built a season team
 * AND played a Match Day. Used to gate league join/request actions.
 */
export function useEngagementStatus() {
  const { user, getAuthHeader } = useAuth();
  const [status, setStatus] = useState({
    loaded: false,
    hasSeasonTeam: false,
    hasDailyTeam: false,
  });

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus({ loaded: true, hasSeasonTeam: false, hasDailyTeam: false });
      return;
    }
    try {
      const res = await axios.get(`${API}/users/engagement-status`, {
        headers: getAuthHeader(),
      });
      setStatus({
        loaded: true,
        hasSeasonTeam: !!res.data?.has_season_team,
        hasDailyTeam: !!res.data?.has_daily_team,
      });
    } catch {
      setStatus({ loaded: true, hasSeasonTeam: false, hasDailyTeam: false });
    }
  }, [user, getAuthHeader]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Helper to apply backend's 403 ENGAGEMENT_REQUIRED payload directly
  const applyBackendStatus = useCallback((detail) => {
    if (!detail) return;
    setStatus({
      loaded: true,
      hasSeasonTeam: !!detail.has_season_team,
      hasDailyTeam: !!detail.has_daily_team,
    });
  }, []);

  return { ...status, refresh, applyBackendStatus };
}

export default function EngagementLockModal({ open, onClose, status }) {
  const navigate = useNavigate();
  if (!open) return null;

  const { hasSeasonTeam, hasDailyTeam } = status || {};

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      data-testid="engagement-lock-modal"
    >
      <div className="bg-slate-900 rounded-2xl max-w-md w-full p-6 border border-slate-700">
        <div className="flex items-start justify-between mb-4">
          <Trophy className="text-emerald-400" size={32} />
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            data-testid="engagement-lock-close"
          >
            <X size={20} />
          </button>
        </div>
        <h3 className="text-xl font-bold text-white mb-2">
          Unlock leagues by playing
        </h3>
        <p className="text-slate-300 mb-4 leading-relaxed">
          To take full advantage of leagues, play the game first. Build your
          season team, pick your XI for Match Day, and earn bonus points with
          the Predictor. Leagues unlock once you're in the game.
        </p>
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800">
            {hasSeasonTeam ? (
              <Check size={20} className="text-emerald-400 shrink-0" />
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-slate-500 shrink-0" />
            )}
            <div className="flex-1">
              <div className="text-white font-medium">Build your season team</div>
              <div className="text-slate-400 text-sm">
                Pick 11 players within your budget
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800">
            {hasDailyTeam ? (
              <Check size={20} className="text-emerald-400 shrink-0" />
            ) : (
              <div className="w-5 h-5 rounded-full border-2 border-slate-500 shrink-0" />
            )}
            <div className="flex-1">
              <div className="text-white font-medium">Play a Match Day</div>
              <div className="text-slate-400 text-sm">
                Submit your XI for today's match
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!hasSeasonTeam && (
            <button
              onClick={() => {
                onClose();
                navigate("/my-team");
              }}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 rounded-xl"
              data-testid="engagement-build-team-btn"
            >
              Build Team
            </button>
          )}
          {hasSeasonTeam && !hasDailyTeam && (
            <button
              onClick={() => {
                onClose();
                navigate("/daily");
              }}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 rounded-xl"
              data-testid="engagement-play-match-day-btn"
            >
              Play Match Day
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
